import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { ScrollReveal, StaggerParent, StaggerItem } from "@/components/shared/ScrollReveal";
import SEO from "@/components/shared/SEO";
import { useLang } from "@/i18n/LanguageContext";

function monogram(client) {
  const words = client.trim().split(/\s+/);
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return words
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function CaseVisual({ caseItem }) {
  const images = caseItem.images || [];

  if (images.length === 0) {
    return (
      <div
        data-testid={`case-visual-${caseItem.id}`}
        className="relative aspect-[16/10] border border-white/10 bg-black overflow-hidden flex items-center justify-center"
      >
        <div className="absolute inset-0 grid-lines opacity-40" />
        <span className="absolute top-0 left-0 h-[3px] w-16 bg-[var(--dp-accent)]" />
        <span className="font-display text-7xl md:text-8xl lg:text-9xl text-white tracking-tight">
          {monogram(caseItem.client)}
        </span>
        <span className="absolute bottom-4 left-4 font-mono-eyebrow text-neutral-500">
          {caseItem.client}
        </span>
      </div>
    );
  }

  const [main, ...rest] = images;
  return (
    <div className="space-y-4">
      <div
        data-testid={`case-visual-${caseItem.id}`}
        className="relative md:aspect-[16/10] border border-white/10 bg-[#0A0A0A] overflow-hidden p-4 md:p-6 flex items-center justify-center transition-colors duration-500 hover:border-white/25"
      >
        <img
          src={`${process.env.PUBLIC_URL}/images/cases/${main}`}
          alt={caseItem.client}
          className="w-full h-auto md:h-full md:object-contain"
          loading="lazy"
          draggable="false"
        />
      </div>
      {rest.length > 0 && (
        <div className="grid grid-cols-2 gap-4">
          {rest.map((img) => (
            <div
              key={img}
              data-testid={`case-visual-${caseItem.id}-extra`}
              className="md:aspect-[16/10] border border-white/10 bg-[#0A0A0A] overflow-hidden p-3 flex items-center justify-center transition-colors duration-500 hover:border-white/25"
            >
              <img
                src={`${process.env.PUBLIC_URL}/images/cases/${img}`}
                alt={caseItem.client}
                className="w-full h-auto md:h-full md:object-contain"
                loading="lazy"
                draggable="false"
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function CaseHistoryPage() {
  const { t } = useLang();
  const ch = t.caseHistory;

  return (
    <main data-testid="case-history-page" className="bg-black text-white">
      <SEO title={t.seo.caseHistory.title} description={t.seo.caseHistory.desc} path="/case-history" />
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 pt-36 md:pt-44 pb-28">
        <ScrollReveal>
          <Link
            to="/"
            data-testid="case-history-back-link"
            className="inline-flex items-center gap-2 font-mono-eyebrow text-neutral-500 hover:text-white transition-colors mb-10"
          >
            <ArrowLeft size={14} /> Home
          </Link>
          <p className="font-mono-eyebrow text-[var(--dp-accent)] mb-6">{ch.eyebrow}</p>
          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight whitespace-pre-line">
            {ch.title.split("\n").map((line, i, arr) =>
              i === arr.length - 1 ? (
                <span key={i} className="font-serif-italic text-[var(--dp-accent)]">
                  {line}
                </span>
              ) : (
                <span key={i}>
                  {line}
                  <br />
                </span>
              )
            )}
          </h1>
          <p className="text-neutral-400 text-lg leading-relaxed mt-8 max-w-2xl">{ch.intro}</p>
          <p data-testid="case-history-clients-intro" className="mt-10 text-sm text-neutral-500">
            {ch.clientsIntro}
          </p>
        </ScrollReveal>

        <StaggerParent className="mt-16 md:mt-24" stagger={0.15}>
          {ch.cases.map((c, i) => (
            <StaggerItem key={c.id}>
              <article
                data-testid={`case-${c.id}`}
                className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 py-14 md:py-20 border-t border-white/10 items-center"
              >
                <div className={`lg:col-span-5 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                  <p className="font-mono-eyebrow text-[var(--dp-accent)] mb-5">
                    {c.services.join(" · ")}
                  </p>
                  <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-none tracking-tight">
                    {c.client}
                  </h2>
                  <p className="text-neutral-400 text-[15px] md:text-base leading-relaxed mt-6 max-w-md">
                    {c.description}
                  </p>
                  {c.clients && (
                    <p
                      data-testid={`case-clients-${c.id}`}
                      className="mt-6 max-w-md text-sm leading-relaxed text-neutral-300"
                    >
                      <span className="block mb-2 text-sm text-neutral-500">
                        {ch.clientsLabel}
                      </span>
                      {c.clientsBreak ? (
                        <>
                          {c.clients.slice(0, c.clientsBreak).join(" · ")}
                          <br />
                          {c.clients.slice(c.clientsBreak).join(" · ")}
                        </>
                      ) : (
                        c.clients.join(" · ")
                      )}
                    </p>
                  )}
                  {c.url && (
                    <a
                      href={c.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`case-link-${c.id}`}
                      className="inline-flex items-center gap-2 mt-7 font-mono-eyebrow text-white link-underline hover:text-[var(--dp-accent)] transition-colors"
                    >
                      {ch.visitSite} <ArrowUpRight size={13} />
                    </a>
                  )}
                </div>
                <div className={`lg:col-span-7 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
                  <CaseVisual caseItem={c} />
                </div>
              </article>
            </StaggerItem>
          ))}
        </StaggerParent>
      </div>
    </main>
  );
}
